import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Calendar, Target, Sparkles } from 'lucide-react';
import '../../styles/design-system.css';

const FreedomCalculator = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    monthlyExpenses: '',
    currentSavings: '',
    monthlyInvestment: '',
    expectedReturn: '8' // % ao ano
  });
  const [result, setResult] = useState(null);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const parseCurrency = (value) => {
    return parseFloat(value.replace(/\D/g, '')) / 100 || 0;
  };

  const handleCurrencyChange = (field, value) => {
    let numValue = value.replace(/\D/g, '');
    if (numValue) {
      numValue = (parseInt(numValue) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    }
    setData({ ...data, [field]: numValue });
  };

  const calculateFreedom = () => {
    const expenses = parseCurrency(data.monthlyExpenses);
    const savings = parseCurrency(data.currentSavings);
    const monthly = parseCurrency(data.monthlyInvestment);
    const rate = parseFloat(data.expectedReturn) / 100 / 12; // Taxa mensal

    // Meta: patrimônio que gera renda passiva = despesas mensais
    // Usando regra dos 4%: patrimônio necessário = despesas anuais / 0.04
    const targetWealth = (expenses * 12) / 0.04;
    
    // Calcular tempo para alcançar usando fórmula de juros compostos
    // FV = PV(1+r)^n + PMT * ((1+r)^n - 1) / r
    // Resolver para n quando FV = targetWealth
    
    let months = 0;
    let currentWealth = savings;
    
    while (currentWealth < targetWealth && months < 600) { // Max 50 anos
      currentWealth = currentWealth * (1 + rate) + monthly;
      months++;
    }

    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    // Calcular patrimônio final projetado
    const projectedWealth = currentWealth;

    // Renda passiva mensal estimada (4% ao ano)
    const passiveIncome = targetWealth * 0.04 / 12;

    setResult({
      targetWealth,
      years,
      months: remainingMonths,
      totalMonths: months,
      projectedWealth,
      passiveIncome,
      monthlyExpenses: expenses,
      savingsRate: (monthly / (expenses + monthly)) * 100
    });
    setStep(3);
  };

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="mb-6">
        <label className="body-small block mb-2">Qual seu gasto mensal total?</label>
        <input
          type="text"
          value={data.monthlyExpenses}
          onChange={(e) => handleCurrencyChange('monthlyExpenses', e.target.value)}
          placeholder="R$ 0,00"
          className="w-full px-4 py-3 rounded-xl text-lg"
          style={{ 
            background: 'var(--bg-section)', 
            border: '2px solid var(--border-input)',
            outline: 'none'
          }}
        />
        <p className="caption mt-2">Inclua todas as despesas: moradia, alimentação, transporte, lazer, etc.</p>
      </div>

      <div className="mb-6">
        <label className="body-small block mb-2">Quanto você já tem guardado/investido?</label>
        <input
          type="text"
          value={data.currentSavings}
          onChange={(e) => handleCurrencyChange('currentSavings', e.target.value)}
          placeholder="R$ 0,00"
          className="w-full px-4 py-3 rounded-xl text-lg"
          style={{ 
            background: 'var(--bg-section)', 
            border: '2px solid var(--border-input)',
            outline: 'none'
          }}
        />
      </div>

      <button
        onClick={() => setStep(2)}
        disabled={!data.monthlyExpenses}
        className="btn-primary w-full"
        style={{ opacity: data.monthlyExpenses ? 1 : 0.5 }}
      >
        Continuar
      </button>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="mb-6">
        <label className="body-small block mb-2">Quanto você consegue investir por mês?</label>
        <input
          type="text"
          value={data.monthlyInvestment}
          onChange={(e) => handleCurrencyChange('monthlyInvestment', e.target.value)}
          placeholder="R$ 0,00"
          className="w-full px-4 py-3 rounded-xl text-lg"
          style={{ 
            background: 'var(--bg-section)', 
            border: '2px solid var(--border-input)',
            outline: 'none'
          }}
        />
      </div>

      <div className="mb-6">
        <label className="body-small block mb-2">Rentabilidade esperada (% ao ano)</label>
        <div className="flex gap-2">
          {['6', '8', '10', '12'].map(rate => (
            <button
              key={rate}
              onClick={() => setData({ ...data, expectedReturn: rate })}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                data.expectedReturn === rate ? 'text-white' : ''
              }`}
              style={{ 
                background: data.expectedReturn === rate ? 'var(--text-primary)' : 'var(--bg-section)',
                border: '1px solid var(--border-light)'
              }}
            >
              {rate}%
            </button>
          ))}
        </div>
        <p className="caption mt-2">
          6-8%: Conservador | 10-12%: Moderado/Arrojado
        </p>
      </div>

      <div className="voice-card accent-grey mb-4">
        <p className="caption">
          💡 <strong>Dica:</strong> A liberdade financeira é alcançada quando seus investimentos 
          geram renda suficiente para cobrir todas as suas despesas.
        </p>
      </div>

      <div className="flex gap-3">
        <button onClick={() => setStep(1)} className="btn-secondary flex-1">
          Voltar
        </button>
        <button
          onClick={calculateFreedom}
          disabled={!data.monthlyInvestment}
          className="btn-primary flex-1"
          style={{ opacity: data.monthlyInvestment ? 1 : 0.5 }}
        >
          Calcular
        </button>
      </div>
    </motion.div>
  );

  const renderResult = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      {/* Main Result */}
      <div 
        className="text-center p-6 rounded-xl mb-6"
        style={{ background: 'var(--gradient-hero-subtle)' }}
      >
        <Sparkles size={32} className="mx-auto mb-3" style={{ color: 'var(--accent-purple-400)' }} />
        <p className="body-small mb-2">Você alcançará a liberdade financeira em</p>
        <div className="heading-hero mb-2">
          {result.years > 0 && <span>{result.years} anos</span>}
          {result.months > 0 && <span> e {result.months} meses</span>}
        </div>
        {result.totalMonths >= 600 && (
          <p className="caption text-red-500">
            ⚠️ Mais de 50 anos. Considere aumentar seus investimentos mensais.
          </p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="voice-card accent-purple">
          <Target size={20} className="mb-2" />
          <p className="caption">Patrimônio Necessário</p>
          <p className="heading-3">{formatCurrency(result.targetWealth)}</p>
        </div>
        <div className="voice-card accent-green">
          <TrendingUp size={20} className="mb-2" />
          <p className="caption">Renda Passiva Mensal</p>
          <p className="heading-3">{formatCurrency(result.passiveIncome)}</p>
        </div>
        <div className="voice-card accent-blue">
          <Calendar size={20} className="mb-2" />
          <p className="caption">Taxa de Poupança</p>
          <p className="heading-3">{result.savingsRate.toFixed(1)}%</p>
        </div>
        <div className="voice-card accent-orange">
          <Sparkles size={20} className="mb-2" />
          <p className="caption">Seus Gastos Mensais</p>
          <p className="heading-3">{formatCurrency(result.monthlyExpenses)}</p>
        </div>
      </div>

      {/* Tips */}
      <div className="voice-card accent-grey mb-4">
        <p className="body-small font-medium mb-2">📖 Princípio Bíblico</p>
        <p className="caption italic">
          "Os planos do diligente tendem à abundância, mas a pressa excessiva leva à pobreza." 
          - Provérbios 21:5
        </p>
      </div>

      {/* Acceleration Tips */}
      <div className="p-4 rounded-xl mb-4" style={{ background: 'var(--bg-section)' }}>
        <p className="body-small font-medium mb-2">🚀 Como acelerar?</p>
        <ul className="space-y-1">
          <li className="caption flex items-start gap-2">
            <span>•</span>
            <span>Aumente sua renda com habilidades extras</span>
          </li>
          <li className="caption flex items-start gap-2">
            <span>•</span>
            <span>Reduza despesas desnecessárias</span>
          </li>
          <li className="caption flex items-start gap-2">
            <span>•</span>
            <span>Reinvista os dividendos</span>
          </li>
          <li className="caption flex items-start gap-2">
            <span>•</span>
            <span>Busque conhecimento financeiro</span>
          </li>
        </ul>
      </div>

      <div className="flex gap-3">
        <button onClick={() => { setStep(1); setResult(null); }} className="btn-secondary flex-1">
          Recalcular
        </button>
        <button onClick={onClose} className="btn-primary flex-1">
          Quero Mentoria
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'var(--accent-green-200)' }}
        >
          <TrendingUp size={24} style={{ color: 'var(--text-primary)' }} />
        </div>
        <div>
          <h3 className="heading-2">Calculadora de Liberdade Financeira</h3>
          <p className="caption">Descubra quando você será financeiramente livre</p>
        </div>
      </div>

      {/* Progress */}
      {step < 3 && (
        <div className="flex gap-2 mb-6">
          {[1, 2].map(s => (
            <div 
              key={s}
              className="flex-1 h-1.5 rounded-full transition-all"
              style={{ background: s <= step ? 'var(--text-primary)' : 'var(--border-light)' }}
            />
          ))}
        </div>
      )}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderResult()}
    </div>
  );
};

export default FreedomCalculator;
